import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { EmptyState, Field, SegmentedControl, inputStyle, tokens } from './ui/controls';

type ModelRow = { s3_uri: string; key: string };
type RunRow = { id: number; run_name?: string; robot_name?: string; start_time?: string | null };
type DatasetRow = { dataset_id: string; name: string; version: string };

function record(value: unknown): Record<string, unknown> {
  if (value != null && typeof value === 'object' && !Array.isArray(value)) {
    return { ...(value as Record<string, unknown>) };
  }
  return {};
}

function pickStyle(active: boolean): React.CSSProperties {
  return {
    textAlign: 'left',
    padding: '0.4rem 0.65rem',
    borderRadius: tokens.radius,
    border: `1px solid ${active ? tokens.primary : tokens.border}`,
    background: active ? tokens.primarySoft : tokens.surface,
    color: tokens.text,
    cursor: 'pointer',
    fontSize: '0.85rem',
    wordBreak: 'break-all',
  };
}

function MatchList({
  empty,
  children,
}: {
  empty: string;
  children: React.ReactNode[];
}) {
  if (children.length === 0) return <EmptyState>{empty}</EmptyState>;
  return (
    <div style={{ display: 'grid', gap: '0.35rem', maxHeight: 220, overflow: 'auto' }}>
      {children}
    </div>
  );
}

export function AutolabelJobForm({
  spec,
  onChange,
}: {
  spec: Record<string, unknown>;
  onChange: (spec: Record<string, unknown>) => void;
}) {
  const data = record(spec.data);
  const model = record(spec.model);
  const selectedKey = String(model.s3_key || '');
  const rawType = String(data.input_type || '');
  const chosen = rawType === 'etl_job' || rawType === 'dataset' ? rawType : '';
  const selectedRun = data.etl_run_id == null ? '' : String(data.etl_run_id);
  const selectedDataset = data.dataset_name && data.dataset_version
    ? `${data.dataset_name}@${data.dataset_version}`
    : '';

  const [modelQuery, setModelQuery] = useState(selectedKey);
  const [models, setModels] = useState<ModelRow[]>([]);
  const [modelError, setModelError] = useState('');
  const [runQuery, setRunQuery] = useState('');
  const [runs, setRuns] = useState<RunRow[]>([]);
  const [runError, setRunError] = useState('');
  const [datasetQuery, setDatasetQuery] = useState('');
  const [datasets, setDatasets] = useState<DatasetRow[]>([]);
  const [datasetError, setDatasetError] = useState('');

  useEffect(() => {
    setModelQuery(selectedKey);
  }, [selectedKey]);

  useEffect(() => {
    api.dataviewer.listInferenceModels()
      .then((res) => {
        setModels(res.data.models || []);
        setModelError('');
      })
      .catch((err: any) => setModelError(err.response?.data?.detail || err.message || 'Could not list models'));
    api.dataviewer.listRegistryDatasets()
      .then((res) => {
        setDatasets(res.data.datasets || []);
        setDatasetError('');
      })
      .catch((err: any) => setDatasetError(err.response?.data?.detail || err.message || 'Could not list datasets'));
  }, []);

  useEffect(() => {
    if (chosen !== 'etl_job') return undefined;
    const handle = window.setTimeout(() => {
      api.dataviewer.getETLRuns({
        status: 'COMPLETED',
        run_name: runQuery.trim() || undefined,
        limit: 50,
        sort_by: 'start_time',
      })
        .then((res) => {
          setRuns((res.data.runs || []) as RunRow[]);
          setRunError('');
        })
        .catch((err: any) => setRunError(err.response?.data?.detail || err.message || 'Could not list ETL jobs'));
    }, 250);
    return () => window.clearTimeout(handle);
  }, [chosen, runQuery]);

  const modelMatches = useMemo(() => {
    const needle = modelQuery.trim().toLowerCase();
    const rows = needle
      ? models.filter((row) => row.key.toLowerCase().includes(needle))
      : models;
    return rows.slice(0, 40);
  }, [models, modelQuery]);

  const datasetMatches = useMemo(() => {
    const needle = datasetQuery.trim().toLowerCase();
    const rows = needle
      ? datasets.filter((row) => `${row.name}@${row.version}`.toLowerCase().includes(needle))
      : datasets;
    return rows.slice(0, 40);
  }, [datasets, datasetQuery]);

  const chooseModel = (row: ModelRow) => {
    const rest = row.s3_uri.replace(/^s3:\/\//, '');
    const slash = rest.indexOf('/');
    const bucket = slash === -1 ? '' : rest.slice(0, slash);
    const key = slash === -1 ? rest : rest.slice(slash + 1);
    onChange({
      ...spec,
      engine_s3_uri: row.s3_uri,
      model: { ...model, s3_bucket: bucket, s3_key: key },
    });
  };

  const chooseType = (inputType: 'etl_job' | 'dataset') => {
    const next: Record<string, unknown> = { ...data, input_type: inputType };
    delete next.s3_urls_file;
    if (inputType === 'etl_job') {
      delete next.dataset_name;
      delete next.dataset_version;
    } else {
      delete next.etl_run_id;
    }
    onChange({ ...spec, data: next });
  };

  const inputOptions: Array<{ id: '' | 'etl_job' | 'dataset'; label: string }> = [
    { id: 'etl_job', label: 'ETL job' },
    { id: 'dataset', label: 'Dataset' },
  ];

  return (
    <div style={{ display: 'grid', gap: '0.9rem' }}>
      <Field label="Model key">
        <input
          value={modelQuery}
          onChange={(event) => setModelQuery(event.target.value)}
          placeholder="Search model key"
          style={inputStyle}
        />
      </Field>
      {modelError ? (
        <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{modelError}</span>
      ) : (
        <MatchList empty="No models match that search.">
          {modelMatches.map((row) => (
            <button
              key={row.s3_uri}
              type="button"
              onClick={() => chooseModel(row)}
              style={pickStyle(row.key === selectedKey)}
            >
              {row.key}
            </button>
          ))}
        </MatchList>
      )}

      <div style={{ display: 'grid', gap: '0.45rem' }}>
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: tokens.textMuted }}>Input</span>
        <SegmentedControl
          options={inputOptions}
          value={chosen}
          onChange={(id) => {
            if (id === 'etl_job' || id === 'dataset') chooseType(id);
          }}
        />
        <span style={{ fontSize: '0.8rem', color: tokens.textMuted }}>
          Label images from a completed ETL job, or from a registry dataset.
        </span>
        {rawType && !chosen ? (
          <span style={{ fontSize: '0.8rem', color: tokens.danger }}>
            This template used {rawType}. Choose an ETL job or a dataset.
          </span>
        ) : null}
      </div>

      {chosen === 'etl_job' && (
        <div style={{ display: 'grid', gap: '0.45rem' }}>
          <Field label="Completed ETL job">
            <input
              value={runQuery}
              onChange={(event) => setRunQuery(event.target.value)}
              placeholder="Search run name"
              style={inputStyle}
            />
          </Field>
          {runError ? (
            <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{runError}</span>
          ) : (
            <MatchList empty="No completed ETL jobs match that search.">
              {runs.map((run) => (
                <button
                  key={run.id}
                  type="button"
                  onClick={() => onChange({ ...spec, data: { ...data, input_type: 'etl_job', etl_run_id: run.id } })}
                  style={pickStyle(String(run.id) === selectedRun)}
                >
                  {run.run_name || `Run ${run.id}`}
                  {run.robot_name ? ` · ${run.robot_name}` : ''}
                  {run.start_time ? ` · ${String(run.start_time).replace('T', ' ').replace(/\.\d+$/, '')}` : ''}
                </button>
              ))}
            </MatchList>
          )}
        </div>
      )}

      {chosen === 'dataset' && (
        <div style={{ display: 'grid', gap: '0.45rem' }}>
          <Field label="Dataset">
            <input
              value={datasetQuery}
              onChange={(event) => setDatasetQuery(event.target.value)}
              placeholder="Search dataset"
              style={inputStyle}
            />
          </Field>
          {datasetError ? (
            <span style={{ fontSize: '0.85rem', color: tokens.danger }}>{datasetError}</span>
          ) : (
            <MatchList empty="No datasets match that search.">
              {datasetMatches.map((row) => {
                const label = `${row.name}@${row.version}`;
                return (
                  <button
                    key={row.dataset_id}
                    type="button"
                    onClick={() => onChange({
                      ...spec,
                      data: { ...data, input_type: 'dataset', dataset_name: row.name, dataset_version: row.version },
                    })}
                    style={pickStyle(label === selectedDataset)}
                  >
                    {label}
                  </button>
                );
              })}
            </MatchList>
          )}
        </div>
      )}
    </div>
  );
}
